import type { SiteNetworkForm } from "./ServerNetworkEditor";
import { AcceptRa } from "../../../flatbuffers/generated/net-config/accept-ra";
import { emptySiteNetwork } from "./ServerNetworkEditor";

/**
 * Colon- or hyphen-separated six-octet MAC, one separator throughout.
 *
 * Deliberately strict. The cloud looks a server up with an exact match on the
 * lower-cased address, so a value that is merely close registers as no server
 * at all: the edge asks, is told nothing matches, and stops. That failure
 * surfaces at a customer site on a headless box, which is the worst place to
 * discover a transposed digit.
 *
 * Written as two alternatives rather than one `[:-]` class on purpose. A class
 * matches each separator independently, so `00:1b-44:11-3a:b7` passes — six
 * valid octets, and nothing here rewrites the separators, so that string is
 * stored verbatim and never matches what the edge reports.
 */
export const MAC_PATTERN =
  /^(?:[0-9A-Fa-f]{2}:){5}[0-9A-Fa-f]{2}$|^(?:[0-9A-Fa-f]{2}-){5}[0-9A-Fa-f]{2}$/;

/**
 * Read a field the API may return as a string, a byte array, or not at all.
 *
 * Server fields are typed `string | Uint8Array | null` because the same
 * records arrive over both the JSON and the FlatBuffer paths.
 */
export const asText = (value: unknown): string => {
  if (typeof value === "string") return value;
  // ArrayBuffer.isView, not `instanceof Uint8Array`. A typed array built in
  // another realm — Node's TextEncoder under jsdom, an iframe, a worker — is
  // not an instance of *this* realm's Uint8Array, so the instanceof check
  // silently returns "" for a perfectly good byte string.
  if (ArrayBuffer.isView(value)) {
    const view = value as ArrayBufferView;
    return new TextDecoder().decode(new Uint8Array(view.buffer, view.byteOffset, view.byteLength));
  }

  return "";
};

const splitList = (value: string): string[] =>
  value
    // Whitespace as well as commas: a column pasted out of a spreadsheet
    // arrives newline-separated, and one long string is sent to the edge as a
    // single address that matches nothing.
    .split(/[\s,]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const joinList = (value: unknown): string =>
  Array.isArray(value)
    ? value
        .map((item) => asText(item))
        .filter(Boolean)
        .join(", ")
    : "";

/**
 * Read a positive integer under either spelling.
 *
 * Both spellings, because the same field arrives two ways: the form builds the
 * snake_case JSON shape, and the FlatBuffer decode hands back the object API's
 * camelCase. Reading only one of them loses the value silently — a route keeps
 * its destination and gateway and quietly forgets which table it belongs to,
 * which is the difference between policy routing and one default route.
 *
 * Zero reads as unset: the schema's numeric fields have no null, so an absent
 * table id and a table id of 0 are the same bytes on the wire.
 */
const intOrNull = (row: Record<string, unknown>, snake: string, camel: string): number | null => {
  const raw = row[snake] ?? row[camel];

  return typeof raw === "number" && raw > 0 ? raw : null;
};

/**
 * Read the router-advertisement setting back off the wire.
 *
 * The GET answers in FlatBuffers, so this arrives as the enum's number, and
 * `Boolean(2)` is `true` — a NIC that deliberately refuses advertisements came
 * back as one that accepts them. An operator opening such a server, changing
 * only its IP, and saving would hand the box a configuration that takes a
 * SLAAC address and a v6 default route from whatever is advertising.
 *
 * A boolean is still accepted, because that is what the field was before it
 * became an enum and a draft saved then is still a draft. A legacy `false` is
 * that draft's deliberate refusal and must resolve to DISABLED — collapsing
 * it into UNSET the way a genuinely absent value does would silently hand
 * such a NIC back the kernel default.
 */
export const acceptRaFromApi = (value: unknown): AcceptRa => {
  if (value === true) return AcceptRa.ENABLED;
  if (value === false) return AcceptRa.DISABLED;
  if (value === AcceptRa.ENABLED || value === AcceptRa.DISABLED) return value;

  return AcceptRa.UNSET;
};

/**
 * Turn the editor's form state into the request body's site_network.
 *
 * Returns null when nothing is configured, and that distinction matters: null
 * means "no site network", which the edge reads as "keep whatever the gold
 * image was built with". An empty object would mean "apply a network with no
 * interfaces in it", which is not the same thing at all.
 */
const asRow = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const originalRows = (original: unknown, key: string): Record<string, unknown>[] => {
  const raw = asRow(original)[key];

  return Array.isArray(raw) ? raw.map(asRow) : [];
};

export const siteNetworkToApi = (
  network: SiteNetworkForm,
  original?: unknown,
): Record<string, unknown> | null => {
  const hasRows =
    network.nics.length > 0 ||
    network.bonds.length > 0 ||
    network.vlans.length > 0 ||
    network.routes.length > 0 ||
    network.rules.length > 0;

  if (
    !hasRows &&
    !network.probeTarget.trim() &&
    !network.allowedCidrs.trim() &&
    !network.nameservers.trim() &&
    !network.searchDomains.trim()
  ) {
    return null;
  }

  return {
    // Spread first, overlaid below. The request replaces the whole value and
    // this editor models only part of it, so anything not modelled — the
    // resolvers, a NIC's name/set_name/optional, most of a bond's settings —
    // has to be carried across or a no-op save silently rewrites the box's
    // network.
    ...asRow(original),
    nics: network.nics.map((nic, index) => ({
      ...originalRows(original, "nics")[index],
      id: nic.id.trim(),
      mac: nic.mac.trim().toLowerCase(),
      name: nic.name.trim(),
      set_name: nic.setName.trim(),
      optional: nic.optional,
      role: nic.role.trim(),
      dhcp4: nic.dhcp4,
      dhcp6: nic.dhcp6,
      accept_ra: nic.acceptRa,
      addresses: splitList(nic.addresses),
      gateway4: nic.gateway4.trim(),
      gateway6: nic.gateway6.trim(),
      mtu: nic.mtu ?? 0,
    })),
    bonds: network.bonds.map((bond, index) => ({
      ...originalRows(original, "bonds")[index],
      id: bond.id.trim(),
      members: splitList(bond.members),
      mode: bond.mode,
      lacp_rate: bond.lacpRate,
      hash_policy: bond.hashPolicy,
      mii_interval: bond.miiInterval ?? 0,
      up_delay: bond.upDelay ?? 0,
      down_delay: bond.downDelay ?? 0,
      min_links: bond.minLinks ?? 0,
      primary: bond.primary.trim(),
      dhcp4: bond.dhcp4,
      dhcp6: bond.dhcp6,
      accept_ra: bond.acceptRa,
      addresses: splitList(bond.addresses),
      gateway4: bond.gateway4.trim(),
      gateway6: bond.gateway6.trim(),
      mtu: bond.mtu ?? 0,
    })),
    vlans: network.vlans.map((vlan, index) => ({
      ...originalRows(original, "vlans")[index],
      id: vlan.id.trim(),
      tag: vlan.tag ?? 0,
      link: vlan.link.trim(),
      dhcp4: vlan.dhcp4,
      dhcp6: vlan.dhcp6,
      accept_ra: vlan.acceptRa,
      addresses: splitList(vlan.addresses),
      gateway4: vlan.gateway4.trim(),
      gateway6: vlan.gateway6.trim(),
      mtu: vlan.mtu ?? 0,
    })),
    routes: network.routes.map((route, index) => ({
      ...originalRows(original, "routes")[index],
      on: route.on.trim(),
      to: route.to.trim(),
      via: route.via.trim(),
      table_id: route.tableId ?? 0,
      metric: route.metric ?? 0,
      on_link: route.onLink,
    })),
    rules: network.rules.map((rule, index) => ({
      ...originalRows(original, "rules")[index],
      on: rule.on.trim(),
      from: rule.from.trim(),
      to: rule.to.trim(),
      table_id: rule.tableId ?? 0,
      priority: rule.priority ?? 0,
      mark: rule.mark ?? 0,
    })),
    probe_target: network.probeTarget.trim(),
    allowed_cidrs: splitList(network.allowedCidrs),
    nameservers: splitList(network.nameservers),
    search_domains: splitList(network.searchDomains),
  };
};

/** Turn an API site_network back into editor state. */
export const siteNetworkFromApi = (value: unknown): SiteNetworkForm => {
  if (!value || typeof value !== "object") return emptySiteNetwork();

  const raw = value as Record<string, unknown>;
  const rows = <T>(key: string): T[] => (Array.isArray(raw[key]) ? (raw[key] as T[]) : []);

  return {
    nics: rows<Record<string, unknown>>("nics").map((nic) => ({
      id: asText(nic.id),
      mac: asText(nic.mac),
      name: asText(nic.name),
      setName: asText(nic.set_name ?? nic.setName),
      optional: Boolean(nic.optional),
      role: asText(nic.role) || "standalone",
      dhcp4: Boolean(nic.dhcp4),
      dhcp6: Boolean(nic.dhcp6),
      acceptRa: acceptRaFromApi(nic.accept_ra ?? nic.acceptRa),
      addresses: joinList(nic.addresses),
      gateway4: asText(nic.gateway4),
      gateway6: asText(nic.gateway6),
      mtu: intOrNull(nic, "mtu", "mtu"),
    })),
    bonds: rows<Record<string, unknown>>("bonds").map((bond) => ({
      id: asText(bond.id),
      members: joinList(bond.members),
      mode: asText(bond.mode) || "802.3ad",
      lacpRate: asText(bond.lacp_rate ?? bond.lacpRate),
      hashPolicy: asText(bond.hash_policy ?? bond.hashPolicy),
      miiInterval: intOrNull(bond, "mii_interval", "miiInterval"),
      upDelay: intOrNull(bond, "up_delay", "upDelay"),
      downDelay: intOrNull(bond, "down_delay", "downDelay"),
      minLinks: intOrNull(bond, "min_links", "minLinks"),
      primary: asText(bond.primary),
      dhcp4: Boolean(bond.dhcp4),
      dhcp6: Boolean(bond.dhcp6),
      acceptRa: acceptRaFromApi(bond.accept_ra ?? bond.acceptRa),
      addresses: joinList(bond.addresses),
      gateway4: asText(bond.gateway4),
      gateway6: asText(bond.gateway6),
      mtu: intOrNull(bond, "mtu", "mtu"),
    })),
    vlans: rows<Record<string, unknown>>("vlans").map((vlan) => ({
      id: asText(vlan.id),
      tag: intOrNull(vlan, "tag", "tag"),
      link: asText(vlan.link),
      dhcp4: Boolean(vlan.dhcp4),
      dhcp6: Boolean(vlan.dhcp6),
      acceptRa: acceptRaFromApi(vlan.accept_ra ?? vlan.acceptRa),
      addresses: joinList(vlan.addresses),
      gateway4: asText(vlan.gateway4),
      gateway6: asText(vlan.gateway6),
      mtu: intOrNull(vlan, "mtu", "mtu"),
    })),
    routes: rows<Record<string, unknown>>("routes").map((route) => ({
      on: asText(route.on),
      to: asText(route.to),
      via: asText(route.via),
      tableId: intOrNull(route, "table_id", "tableId"),
      metric: intOrNull(route, "metric", "metric"),
      onLink: Boolean(route.on_link ?? route.onLink),
    })),
    rules: rows<Record<string, unknown>>("rules").map((rule) => ({
      on: asText(rule.on),
      from: asText(rule.from),
      to: asText(rule.to),
      tableId: intOrNull(rule, "table_id", "tableId"),
      priority: intOrNull(rule, "priority", "priority"),
      mark: intOrNull(rule, "mark", "mark"),
    })),
    probeTarget: asText(raw.probe_target ?? raw.probeTarget),
    allowedCidrs: joinList(raw.allowed_cidrs ?? raw.allowedCidrs),
    nameservers: joinList(raw.nameservers),
    searchDomains: joinList(raw.search_domains ?? raw.searchDomains),
  };
};

/**
 * The identity half of a create/update body.
 *
 * Omits a field entirely rather than sending an empty one. The server treats
 * an empty string as "not supplied" precisely so that a client which does not
 * know about these fields cannot null a live server's MAC — sending "" here
 * would work today and quietly rely on that, which is a worse contract than
 * simply not sending it.
 */
export const serverIdentityBody = (
  mac: string,
  serial: string,
  network: SiteNetworkForm,
  original?: unknown,
): Record<string, unknown> => {
  const body: Record<string, unknown> = {};

  if (mac.trim()) body.mac_address = mac.trim().toLowerCase();
  if (serial.trim()) body.serial_number = serial.trim();

  const siteNetwork = siteNetworkToApi(network, original);
  if (siteNetwork) body.site_network = siteNetwork;

  return body;
};
